import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  Fragment,
} from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Transition } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Copy,
  Trash2,
  ChevronDown,
  User2,
  ExternalLink,
  QrCode,
} from "lucide-react";
import WhatsappQrGenerateModal from "./WhatsappQrGenerateModal";
import useClients from "../../Services/ClientServices/ClientConfig/useClientConfig";
import { GlobalContext } from "../../../Context/GlobalContext";
import toast from "react-hot-toast";

const THEME_BLUE_BG = "bg-[#f8fbff]";
const BORDER_SMART = "border-gray-300";

const waSchema = z.object({
  userid: z.string().min(1),
  appId: z.string().min(1, "App ID is required"),
  businessAccId: z.string().min(1, "Business Account ID is required"),
  phoneNo: z.string().min(1, "Phone No. ID is required"),
  token: z.string().min(10, "Token is required"),
});

export default function ClientDetailsTab({
  clientId,
  clientName,
  activeTab,
  busy = {},
  selectedClient = null,
  currentGateway = "",
}) {
  const { AllClients, GateWay } = useContext(GlobalContext) || {};

  const {
    register: registerClient,
    deRegister,
    subscribe,
    activate,
    deactivate,
    fetchWhatsappCredentials,
    updateWhatsappCredentials,
    extendSubscription,
    updatePaymentGateway,
    getWhatsappProfileSubscriptions,
deleteWhatsappProfileSubscriptions,
getFssaiNo,
updateFssaiNo,
} = useClients();

  function MapGateWayName(arr = []) {
    return arr
      .filter((obj) => obj.is_active === "Y" || obj.is_active === true)
      .map((obj) => obj.gateway_name)
      .filter(Boolean);
  }
  // console.log(GateWay)
  const [section, setSection] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [waCredsRaw, setWaCredsRaw] = useState(null);
  const [waLoading, setWaLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [days, setDays] = useState("30");
  const [gateway, setGateway] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showWhatsappQrModal, setShowWhatsappQrModal] = useState(false);
const [fassiLicence, setFassiLicence] = useState("");
const [fassiLoading, setFassiLoading] = useState(false);
const [fassiSaving, setFassiSaving] = useState(false);

  const client = useMemo(() => {
    if (selectedClient) return selectedClient;

    if (!AllClients?.length || !clientId) return null;

    return AllClients.find(
      (c) => String(c.us_code) === String(clientId) || c.us_name === clientName
    );
  }, [selectedClient, AllClients, clientId, clientName]);

  useEffect(() => {
    const incomingGateway =
      client?.payment_gateway || currentGateway || "";

    if (!incomingGateway) {
      setGateway("");
      return;
    }

    const gatewayOptions = MapGateWayName(GateWay || []);

    const matchedGateway =
      gatewayOptions.find(
        (g) =>
          String(g).toLowerCase().trim() ===
          String(incomingGateway).toLowerCase().trim()
      ) || incomingGateway;

    setGateway(matchedGateway);
  }, [client?.payment_gateway, currentGateway, GateWay]);

  const spin = clientId ? busy?.[clientId] : null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({ resolver: zodResolver(waSchema) });

  useEffect(() => {
    if (activeTab !== "WhatsApp" || !clientId) return;
    reset({
      userid: clientId,
      appId: "",
      businessAccId: "",
      phoneNo: "",
      token: "",
    });
    loadWA();
  }, [activeTab, clientId]);

  useEffect(() => {
    subscriptionStatus();
  }, []);

useEffect(() => {
  if (!clientId) return;

  const loadFssaiLicence = async () => {
    setFassiLoading(true);

    try {
      const licence = await getFssaiNo(clientId);
      setFassiLicence(licence || "");
    } catch (error) {
      console.error("Failed to load FSSAI licence:", error);
      setFassiLicence("");
    } finally {
      setFassiLoading(false);
    }
  };

  loadFssaiLicence();
}, [clientId]);
const handleFssaiSave = async () => {
  if (!clientId) {
    toast.error("Client ID is missing");
    return;
  }

  const licence = fassiLicence.trim();

  if (!licence) {
    toast.error("Please enter FSSAI licence number");
    return;
  }

  setFassiSaving(true);

  try {
    const success = await updateFssaiNo({
      user_id: clientId,
      fssai_no: licence,
    });

    if (success) {
      // Fetch again so UI contains the actual value saved by backend
      const response = await getFssaiNo(clientId);

      const updatedLicence =
        response?.fssai_no ??
        response?.fssaiNo ??
        response?.fssai_licence ??
        response?.fssaiLicence ??
        licence;

      setFassiLicence(String(updatedLicence || ""));
    }
  } catch (error) {
    console.error("FSSAI update error:", error);
  } finally {
    setFassiSaving(false);
  }
};

  const loadWA = async () => {
    setWaLoading(true);
    try {
      const creds = await fetchWhatsappCredentials(clientId);
      setWaCredsRaw(creds || null);
      if (creds) {
        reset({
          userid: clientId,
          appId: creds.app_id || "",
          businessAccId: creds.business_acc_id || "",
          phoneNo: creds.phone_no_id || "",
          token: creds.token || "",
        });
      }
    } finally {
      setWaLoading(false);
    }
  };

  const subscriptionStatus = async () => {
    try {
      const status = await getWhatsappProfileSubscriptions(clientId);
      setSubscribed(status);
    } catch (error) {
      console.error("Error fetching subscription status:", error);
    }
  };

  const onSaveWA = async (data) => {
    await updateWhatsappCredentials(data);
    setWaCredsRaw(data);
  };

  const removeWACreds = async () => {
    const payload = {
      userid: clientId,
      appId: "",
      businessAccId: "",
      phoneNo: "",
      token: "",
    };
    await updateWhatsappCredentials(payload);
    setWaCredsRaw(null);
    reset(payload);
  };

  const toggleSection = async (next) => {
    if (section === next) {
      setSection(null);
      return;
    }
    setCopied(false);
    setSection(next);
    if (next === "wa") await loadWA();
  };

  const copyWACreds = async () => {
    await navigator.clipboard.writeText(
      JSON.stringify(waCredsRaw ?? {}, null, 2),
    );
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  const handleGatewaySave = () => {
    const adminId = JSON.parse(localStorage.getItem("Admin") || "{}")?.pkid;
    updatePaymentGateway({
      adminId,
      userid: clientId,
      paymentGateway: gateway,
    });
  };

  if (!client) return null;
  console.log(client, 'clinet>>');
  return (
    <>
    
      {/* CONTENT AREA */}
      <div className="px-0 max-w-[1800px] grid gap-6 lg:grid-cols-[1fr_800px]">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          <Card title="Basic Information">
            <div className="grid gap-2">
              <Info label="ID" value={client.id} />
              <Info label="Organization" value={client.organization_name} />
              <Info label="User Code" value={client.us_code} />
              <Info label="Login ID" value={client.us_loginid} />
              <Info label="Email" value={client.us_email} />
              <Info
                label="Status"
                value={client.us_status === "1" ? "Active" : "Inactive"}
                status={client.us_status === "1" ? "Y" : "N"}
              />
              <Info label="Last Login" value={client.us_last_login} />
              <div title="Account Overview"></div>
            </div>
          </Card>

          <Card title="Account Overview">
            <div className="grid gap-2">
              <Info label="Wallet Balance" value={`₹${client.balance || 0}`} />
              <Info label="Campaigns" value={client.totalCampaigns ?? 0} />
              <Info label="Templates" value={client.totalTemplates ?? 0} />
              {client.deep_link && (
                <div
                  className={`flex items-center justify-between rounded-xl ${THEME_BLUE_BG} border ${BORDER_SMART} px-4 py-3 text-sm`}
                >
                  <span className="text-gray-500">Deep Link</span>
                  <a
                    className="flex items-center gap-1 font-semibold text-[#01C37D] hover:underline"
                    href={client.deep_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open <ExternalLink size={14} />
                  </a>
                </div>
              )}
            </div>
            {client.qr_code && (
              <div
                className={`flex items-start justify-between rounded-xl ${THEME_BLUE_BG} border ${BORDER_SMART} px-4 py-3 text-sm`}
              >
                <span className="text-gray-500">QR Code</span>
                <img
                  src={client?.qr_code}
                  alt="QR"
                  className="w-full object-contain"
                  style={{ height: "100px", width: "100px" }}
                />
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          <section
            className={`rounded-2xl border ${BORDER_SMART} bg-white p-6 shadow-sm`}
          >
            <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
              Management Actions
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              <div>
                {subscribed == true ? (
                  <Btn success onClick={() => setConfirm("unsubscribe")}>
                    Subscribed
                  </Btn>
                ) : (
                  <Btn
                    onClick={async () => {
                      try {
                        await subscribe(clientId);
                        subscriptionStatus();
                      } catch (error) {
                        console.error("Delete API failed", error);
                      }
                    }}
                  >
                    Subscribe
                  </Btn>
                )}
              </div>
              {/*  */}

              <Btn
                disabled={spin === "register"}
                onClick={async () => {
                  try {
                    await registerClient(clientId);
                    setRegistered(true);
                  } catch (error) {
                    console.error("Register API failed", error);
                  }
                }}
              >
                {spin === "register" ? "Registering…" : "Register"}
              </Btn>

              <Btn
                danger
                disabled={spin === "deregister"}
                onClick={async () => {
                  try {
                    await deRegister(clientId);
                    setRegistered(false);
                  } catch (error) {
                    console.error("Deregister API failed", error);
                  }
                }}
              >
                {spin === "deregister" ? "Deregistering…" : "Deregister"}
              </Btn>
              {client.us_status === "1" ? (
                <Btn danger onClick={() => setConfirm("deactivate")}>
                  Deactivate
                </Btn>
              ) : (
                <Btn success onClick={() => setConfirm("activate")}>
                  Activate
                </Btn>
              )}
              <Btn onClick={() => setShowWhatsappQrModal(true)}>
                <span className="inline-flex items-center gap-2">
                  <QrCode size={15} />
                  Generate QR
                </span>
              </Btn>
            </div>

            <div className="space-y-3">
              <AccordionButton
                active={section === "wa"}
                onClick={() => toggleSection("wa")}
                label="WhatsApp Config"
              />
              <AccordionButton
                active={section === "up"}
                onClick={() => toggleSection("up")}
                label="Subscription"
              />
              <AccordionButton
                active={section === "gw"}
                onClick={() => toggleSection("gw")}
                label="Payment Gateway"
              />
            </div>

            {/* EXPANDABLE SECTIONS */}
            <ExpandTransition show={section === "wa"}>
              <Section>
                <HeaderRow
                  title="WhatsApp Credentials"
                  onCopy={copyWACreds}
                  onRemove={() => setConfirm("remove_wa")}
                />
                {copied && <SuccessText />}
                <form
                  className="grid gap-3"
                  onSubmit={handleSubmit(
                    onSaveWA,
                    (formErrors) => {
                      console.log("WhatsApp form validation failed:", formErrors);
                    }
                  )}
                >
                  <input type="hidden" {...register("userid")} />
                  <Field label="App ID" error={errors.appId?.message}>
                    <input {...inputCls} {...register("appId")} />
                  </Field>
                  <Field
                    label="Business Account ID"
                    error={errors.businessAccId?.message}
                  >
                    <input {...inputCls} {...register("businessAccId")} />
                  </Field>
                  <Field label="Phone No. ID" error={errors.phoneNo?.message}>
                    <input {...inputCls} {...register("phoneNo")} />
                  </Field>
                  <Field label="Access Token" error={errors.token?.message}>
                    <textarea
                      rows={3}
                      {...textareaCls}
                      {...register("token")}
                    />
                  </Field>
                  <ActionRow>
                    <Primary type="submit">Save Changes</Primary>
                  </ActionRow>
                </form>
              </Section>
            </ExpandTransition>

            <ExpandTransition show={section === "up"}>
              <Section>
                <div className="grid gap-3">
                  <ReadOnly label="Target User" value={client.us_code} />
                  <Select
                    label="Extension Period (Days)"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    options={["30", "60", "90", "180", "365"]}
                  />
                </div>
                <ActionRow>
                  <Primary
                    onClick={() =>
                      extendSubscription({ userid: client.us_code, days })
                    }
                  >
                    Confirm Extension
                  </Primary>
                </ActionRow>
              </Section>
            </ExpandTransition>

            <ExpandTransition show={section === "gw"}>
              <Section>
                <div className="grid gap-3">
                  <ReadOnly label="Target User" value={client.us_code} />
                  <Select
                    label="Active Gateway"
                    value={gateway}
                    onChange={(e) => setGateway(e.target.value)}
                    options={MapGateWayName(GateWay)}
                  />
                </div>
                <ActionRow>
                  <Primary onClick={handleGatewaySave}>Save Gateway</Primary>
                </ActionRow>
              </Section>
            </ExpandTransition>
          </section>
          {/* FASSI LICENCE */}
<section
  className={`rounded-2xl border ${BORDER_SMART} bg-white p-6 shadow-sm`}
>
  <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
    FASSI Licence
  </div>

  <div className="space-y-4">
    <div>
      <label className="mb-1.5 ml-1 block text-xs font-semibold text-gray-500">
        FASSI Licence Number
      </label>
<input
  type="text"
  value={fassiLicence}
  onChange={(e) => setFassiLicence(e.target.value)}
  placeholder={
    fassiLoading
      ? "Loading FSSAI licence..."
      : "Enter FSSAI licence number"
  }
  disabled={fassiLoading || fassiSaving}
  className="h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm outline-none transition-all focus:border-[#01C37D] focus:ring-4 focus:ring-[#01C37D]/5 disabled:cursor-not-allowed disabled:bg-gray-50"
/>
    </div>

    <div className="flex justify-end pt-1">
<Primary
  type="button"
  onClick={handleFssaiSave}
  disabled={fassiLoading || fassiSaving}
>
  {fassiSaving ? "Saving..." : "Save / Update"}
</Primary>
    </div>
  </div>
</section>
        </div>
      </div>

      {/* MODALS */}
      <Confirm
        open={confirm === "activate"}
        title="Activate client account?"
        onConfirm={() => activate(client.us_code)}
        onClose={() => setConfirm(null)}
      />
      <Confirm
        open={confirm === "deactivate"}
        title="Deactivate client account?"
        danger
        onConfirm={() => deactivate(client.us_code)}
        onClose={() => setConfirm(null)}
      />
      <Confirm
        open={confirm === "remove_wa"}
        title="Wipe WhatsApp credentials?"
        danger
        onConfirm={removeWACreds}
        onClose={() => setConfirm(null)}
      />
      <Unsubscribe
        open={confirm === "unsubscribe"}
        title="Unsubscribe client services?"
        danger
        onConfirm={async () => {
          try {
            await deleteWhatsappProfileSubscriptions(clientId);
            subscriptionStatus();
            setShowSubscriptionModal(false);
          } catch (error) {
            console.error("Delete API failed", error);
          }
        }}
        onClose={() => setConfirm(null)}
      />
      <WhatsappQrGenerateModal
        open={showWhatsappQrModal}
        onClose={() => setShowWhatsappQrModal(false)}
        clientId={client?.us_code || clientId}
        clientName={client?.organization_name || client?.us_name || clientName}
        defaultLoginNumber={client?.us_loginid || client?.mobile || ""}
      />

    </>
  );
}

const inputCls = {
  className: `h-11 w-full rounded-xl border ${BORDER_SMART} bg-white px-3 text-sm outline-none focus:border-[#01C37D] focus:ring-4 focus:ring-[#01C37D]/5 transition-all`,
};

const textareaCls = {
  className: `w-full rounded-xl border ${BORDER_SMART} bg-white px-3 py-2 text-sm outline-none focus:border-[#01C37D] focus:ring-4 focus:ring-[#01C37D]/5 transition-all`,
};

function Card({ title, children }) {
  return (
    <div
      className={`rounded-2xl bg-white p-6 shadow-sm border ${BORDER_SMART}`}
    >
      <div className="mb-4 text-xs font-bold uppercase tracking-widest text-gray-400">
        {title}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Info({ label, value, status }) {
  const isStatus = status !== undefined;
  const colorClass =
    status === "Y"
      ? "text-emerald-700 bg-emerald-50 border-emerald-200"
      : status === "N"
        ? "text-rose-700 bg-rose-50 border-rose-200"
        : "text-gray-900 border-gray-300";

  return (
    <div
      className={`flex items-center justify-between rounded-xl ${THEME_BLUE_BG} border ${BORDER_SMART} px-4 py-3 text-sm`}
    >
      <span className="text-gray-500 font-medium">{label}</span>
      <span
        className={`font-semibold ${isStatus ? "px-2 py-0.5 rounded-lg border " + colorClass : "text-gray-900"}`}
      >
        {value ?? "—"}
      </span>
    </div>
  );
}

function Btn({ children, success, danger, ...props }) {
  const base =
    "h-10 rounded-xl px-5 text-sm font-semibold transition-all active:scale-95 disabled:opacity-50 border";
  const cls = success
    ? `${base} bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600 shadow-sm`
    : danger
      ? `${base} bg-rose-500 text-white border-rose-600 hover:bg-rose-600 shadow-sm`
      : `${base} ${BORDER_SMART} bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400`;

  return (
    <button className={cls} {...props}>
      {children}
    </button>
  );
}

function AccordionButton({ active, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex h-12 w-full items-center justify-between rounded-xl px-4 text-sm transition-all border
        ${active ? "bg-[#01C37D] text-white border-[#00ab6d] shadow-lg shadow-[#01C37D]/20" : `${THEME_BLUE_BG} ${BORDER_SMART} hover:bg-blue-50 text-gray-700`}
      `}
    >
      <span className="font-semibold">{label}</span>
      <ChevronDown
        size={18}
        className={`transition-transform duration-300 ${active ? "rotate-180" : ""}`}
      />
    </button>
  );
}

function Section({ children }) {
  return (
    <div
      className={`mt-4 rounded-2xl ${THEME_BLUE_BG} p-5 border ${BORDER_SMART} space-y-4 animate-in fade-in slide-in-from-top-2`}
    >
      {children}
    </div>
  );
}

function HeaderRow({ title, onCopy, onRemove }) {
  return (
    <div
      className={`flex justify-between items-center border-b ${BORDER_SMART} pb-3 mb-2`}
    >
      <div className="text-sm font-bold text-gray-700">{title}</div>
      <div className="flex gap-2">
        <IconBtn onClick={onCopy} title="Copy JSON">
          <Copy size={14} />
        </IconBtn>
        <IconBtn danger onClick={onRemove} title="Delete">
          <Trash2 size={14} />
        </IconBtn>
      </div>
    </div>
  );
}

function SuccessText() {
  return (
    <div className="text-[11px] font-bold text-emerald-600 uppercase tracking-tight">
      Copied to clipboard!
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-semibold text-gray-500 ml-1">
        {label}
      </div>
      {children}
      {error && (
        <div className="mt-1.5 text-xs font-medium text-rose-500 ml-1">
          {error}
        </div>
      )}
    </label>
  );
}

function ActionRow({ children }) {
  return <div className="flex justify-end pt-2">{children}</div>;
}

function Primary({ children, ...props }) {
  return (
    <button
      className="h-10 rounded-xl bg-[#01C37D] px-6 text-sm font-bold text-white hover:bg-[#00ab6d] transition-all shadow-md shadow-[#01C37D]/10"
      {...props}
    >
      {children}
    </button>
  );
}

function ReadOnly({ label, value }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-semibold text-gray-500 ml-1">
        {label}
      </div>
      <div
        className={`h-11 w-full rounded-xl border ${BORDER_SMART} bg-white/50 flex items-center px-3 text-sm text-gray-600 font-medium`}
      >
        {value}
      </div>
    </label>
  );
}

function Select({ label, value, onChange, options = [] }) {
  return (
    <label className="block">
      <div className="mb-1.5 text-xs font-semibold text-gray-500 ml-1">
        {label}
      </div>

      <div className="relative">
        <select
          value={value || ""}
          onChange={onChange}
          className={`h-11 w-full appearance-none rounded-xl border ${BORDER_SMART} bg-white px-3 text-sm outline-none focus:border-[#01C37D] transition-all`}
        >
          <option value="">Please Select</option>

          {options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>

        <ChevronDown
          size={14}
          className="absolute right-3 top-4 text-gray-400 pointer-events-none"
        />
      </div>
    </label>
  );
}

function IconBtn({ children, danger, ...props }) {
  return (
    <button
      className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors border ${danger
        ? "bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100"
        : `bg-white ${BORDER_SMART} text-gray-500 hover:bg-gray-50`
        }`}
      {...props}
    >
      {children}
    </button>
  );
}

function ExpandTransition({ show, children }) {
  return (
    <Transition
      show={show}
      as={Fragment}
      enter="transition-all duration-300 ease-out"
      enterFrom="opacity-0 -translate-y-2 max-h-0"
      enterTo="opacity-100 translate-y-0 max-h-[1000px]"
      leave="transition-all duration-200 ease-in"
      leaveFrom="opacity-100 max-h-[1000px]"
      leaveTo="opacity-0 max-h-0"
    >
      <div className="overflow-hidden">{children}</div>
    </Transition>
  );
}

function Confirm({ open, title, danger, onConfirm, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div
        className={`w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl border ${BORDER_SMART} animate-in fade-in zoom-in duration-200`}
      >
        <div
          className={`mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl ${danger ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}
        >
          {danger ? <Trash2 size={28} /> : <User2 size={28} />}
        </div>
        <h3 className="text-center text-lg font-bold text-gray-900">{title}</h3>
        <p className="mt-2 text-center text-sm text-gray-500">
          Are you sure you want to proceed? This action may affect client
          services.
        </p>
        <div className="mt-8 flex flex-col gap-2">
          <button
            onClick={() => {
              onConfirm?.();
              onClose();
            }}
            className={`h-12 w-full rounded-2xl font-bold text-white transition-all active:scale-95 ${danger ? "bg-rose-500 hover:bg-rose-600" : "bg-[#01C37D] hover:bg-[#00ab6d]"}`}
          >
            Confirm Action
          </button>
          <button
            onClick={onClose}
            className="h-12 w-full rounded-2xl font-semibold text-gray-500 hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Unsubscribe({ open, title, danger, onConfirm, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div
        className={`w-full max-w-sm rounded-3xl bg-white p-8 shadow-2xl border ${BORDER_SMART} animate-in fade-in zoom-in duration-200`}
      >
        <div
          className={`mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl ${danger ? "bg-rose-100 text-rose-600" : "bg-emerald-100 text-emerald-600"}`}
        >
          {danger ? <Trash2 size={28} /> : <User2 size={28} />}
        </div>
        <h3 className="text-center text-lg font-bold text-gray-900">{title}</h3>
        <p className="mt-2 text-center text-sm text-gray-500">
          Are you sure you want to proceed? This action may affect client
          services.
        </p>
        <div className="mt-8 flex flex-col gap-2">
          <button
            onClick={() => {
              onConfirm?.();
              onClose();
            }}
            className={`h-12 w-full rounded-2xl font-bold text-white transition-all active:scale-95 ${danger ? "bg-rose-500 hover:bg-rose-600" : "bg-[#01C37D] hover:bg-[#00ab6d]"}`}
          >
            Unsubscribe
          </button>
          <button
            onClick={onClose}
            className="h-12 w-full rounded-2xl font-semibold text-gray-500 hover:bg-gray-50 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
